﻿import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../config/Config';
import {
  fetchClientById,
  fetchClientsByEmail,
  fetchClientsByContactEmail,
  fetchWorkspacesByAnchor,
} from '../services/PortalContextRepository.js';

const PortalContext = createContext({ crmClient: null, loading: true, email: '' });

function isHumanProfile(d) {
  const raw = String(d?.type || d?.nature || '').toLowerCase().trim();
  if (!raw) {
    if (d?.qualification?.animal) return false;
    return true;
  }
  const NON_HUMAN = ['animal', 'pj', 'jurídica', 'juridica', 'espolio', 'espólio', 'cnpj', 'empresa', 'nascituro', 'unborn', 'mascote', 'não-humano', 'nao-humano'];
  return !NON_HUMAN.some((kw) => raw.includes(kw));
}

export function PortalProvider({ children, forcedClientId = null }) {
  // [PREVIEW-GUARD] Sinaliza modo Preview Admin globalmente para que
  // portalEventLog.js suprima gravação de eventos no prontuário do cliente.
  // Evita que admin testando inflame a timeline real com eventos falsos.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const wasPreview = Boolean(window.__rda_portal_preview_mode);
    window.__rda_portal_preview_mode = Boolean(forcedClientId);
    return () => {
      if (typeof window === 'undefined') return;
      window.__rda_portal_preview_mode = wasPreview;
    };
  }, [forcedClientId]);

  const [email, setEmail] = useState('');
  const [crmClient, setCrmClient] = useState(null);
  const [profileCandidates, setProfileCandidates] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [humanProfile, setHumanProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const claimsSyncedRef = useRef(false);

  // Admin preview mode: load a specific client directly, bypass auth
  useEffect(() => {
    if (!forcedClientId) return;
    const appId = String(FIREBASE_CONFIG?.projectId || '').trim();
    if (!appId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const client = await fetchClientById(forcedClientId);
        if (alive && client) {
          setCrmClient(client);
          setHumanProfile(client);
        }
      } catch { void 0; } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [forcedClientId]);

  useEffect(() => {
    if (forcedClientId) return; // skip auth when in preview mode
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (u) => {
      const next = u?.email ? String(u.email).trim() : '';
      setEmail(next);

      // Phase 2 ReBAC: sincronizar Custom Claims (rda_clientIds) no JWT
      if (u && next && !claimsSyncedRef.current) {
        claimsSyncedRef.current = true;
        try {
          const fn = httpsCallable(functions, 'syncUserClaims');
          await fn();
          await u.getIdToken(true); // Força refresh do JWT com as novas claims
        } catch (e) {
          console.warn('[PortalContext] syncUserClaims falhou (não-bloqueante):', e?.message || e);
        }
      }
      if (!u) claimsSyncedRef.current = false;

      setAuthReady(true);
    });
    return () => {
      try {
        unsub && unsub();
      } catch (e) {
        void e;
      }
    };
  }, []);

  useEffect(() => {
    if (forcedClientId) return; // skip auth lookup when in preview mode
    let alive = true;

    const run = async () => {
      if (!authReady) return;
      setLoading(true);
      setCrmClient(null);

      const appId = String(FIREBASE_CONFIG?.projectId || '').trim();

      let seedHit = null;

      const storedClientId = (() => {
        try { return String(window.sessionStorage.getItem('portalClientId') || '').trim(); } catch { return ''; }
      })();

      if (storedClientId && appId) {
        try {
          const client = await fetchClientById(storedClientId);
          if (client && client.isDeleted !== true) {
            seedHit = client;
          }
        } catch { }
        try { window.sessionStorage.removeItem('portalClientId'); } catch {}
      }

      const em = String(email || '').trim();
      if (!em) {
        if (!alive) return;
        if (seedHit) { setCrmClient(seedHit); setProfileCandidates(null); }
        setLoading(false);
        return;
      }

      try {
        const hits = seedHit ? [seedHit] : [];

        try {
          const emailHits = await fetchClientsByEmail(em);
          emailHits.forEach((h) => {
            if (!hits.find((x) => x.id === h.id)) hits.push(h);
          });
        } catch (e) {
          void e;
        }

        try {
          const contactHits = await fetchClientsByContactEmail(em);
          contactHits.forEach((h) => {
            if (!hits.find((x) => x.id === h.id)) hits.push(h);
          });
        } catch (e) {
          void e;
        }

        if (!alive) return;

        const humanHits = hits.filter(h => isHumanProfile(h) && h.isDeleted !== true);

        if (humanHits.length > 1) {
          setProfileCandidates(humanHits);
          // LEI DA INÃ‰RCIA: se o usuário JÃ escolheu um perfil, NUNCA o ejetar.
          // Atualiza os dados do perfil corrente com a versão mais recente do banco.
          setCrmClient((prevClient) => {
            if (prevClient) {
              const updatedMe = humanHits.find(p => p.id === prevClient.id);
              return updatedMe || prevClient;
            }
            return null;
          });
        } else if (humanHits.length === 1) {
          setHumanProfile(humanHits[0]);
          setCrmClient(humanHits[0]);
          setProfileCandidates(null);
        } else {
          setCrmClient(null);
          setProfileCandidates(null);
        }
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [email, authReady]);

  // ReBAC: load workspaces by anchor.id â€” NEVER by email
  useEffect(() => {
    let alive = true;
    const appId = String(FIREBASE_CONFIG?.projectId || '').trim();

    const loadWorkspaces = async () => {
      if (!humanProfile?.id || !appId) {
        setWorkspaces([]);
        return;
      }
      const hits = await fetchWorkspacesByAnchor(humanProfile.id);

      if (!alive) return;
      setWorkspaces(hits);
    };

    loadWorkspaces();
    return () => { alive = false; };
  }, [humanProfile?.id]);

  const previewMode = Boolean(forcedClientId);

  // Art.327: flag que indica se o usuário logado é advogado/parceiro (role PARTNER)
  const isPartner = useMemo(() => {
    const roles = Array.isArray(crmClient?.roles) ? crmClient.roles : [];
    return roles.some((r) => String(r || '').toUpperCase() === 'PARTNER');
  }, [crmClient?.roles]);

  const value = useMemo(() => ({
    crmClient, loading, email, profileCandidates, setProfileCandidates,
    workspaces, humanProfile, setHumanProfile, setCrmClient,
    previewMode, isPartner,
  }), [crmClient, loading, email, profileCandidates, workspaces, humanProfile, previewMode, isPartner]);

  if (loading) {
    return (
      <PortalContext.Provider value={value}>
        <div className="border border-gray-200 shadow-sm bg-white rounded-md p-6">
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-[#C5A059]" />
            <div className="text-sm font-bold text-[#20243B]">Carregando seu acesso...</div>
          </div>
        </div>
      </PortalContext.Provider>
    );
  }


  const isOnboardingRoute =
    typeof window !== 'undefined' &&
    window.location.pathname === '/portal/completar-cadastro';

  if (!crmClient && !isOnboardingRoute && !(profileCandidates && profileCandidates.length > 1)) {
    if (forcedClientId) {
      return (
        <PortalContext.Provider value={value}>
          <div className="border border-red-200 shadow-sm bg-red-50 rounded-md p-6 m-4">
            <div className="text-sm font-extrabold text-red-700">Cliente não encontrado</div>
            <div className="mt-2 text-xs text-red-600">
              Nenhum cliente com ID <code className="font-mono bg-red-100 px-1 rounded">{forcedClientId}</code> foi encontrado no Firestore.
            </div>
          </div>
        </PortalContext.Provider>
      );
    }
    return (
      <PortalContext.Provider value={value}>
        <div className="border border-gray-200 shadow-sm bg-white rounded-md p-6">
          <div className="text-sm font-extrabold text-[#20243B]">Sincronizando seus dados</div>
          <div className="mt-2 text-xs text-gray-600">
            Sincronizando seus dados com o escritório. Aguarde liberação do advogado.
          </div>
          {email ? <div className="mt-3 text-[11px] text-gray-500">Login: {email}</div> : null}
        </div>
      </PortalContext.Provider>
    );
  }

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

export function usePortalContext() {
  return useContext(PortalContext);
}
